/* ──────────────────────────────────────────────────────────────────────────
 * Data and privacy architecture — §17.
 *
 * The brief closes the section with the sentence that should govern the
 * whole design:
 *
 *   "The objective is public safety intelligence — not unnecessary
 *    surveillance."
 *
 * Most of §17 is architecture rather than code: encryption at rest,
 * security monitoring and incident response are properties of a deployment,
 * and a browser demo cannot implement them. Claiming otherwise would be the
 * kind of overstatement the section exists to prevent.
 *
 * So this module does two separate things and keeps them clearly apart:
 *
 *   1. ENFORCES the controls that genuinely can be enforced here —
 *      role-based access, classification, field-level redaction, purpose
 *      limitation, retention, and the identity-analytics prohibition.
 *      `redactFor()` actually removes data. It is not a label.
 *
 *   2. DECLARES the controls that can only be architectural, each marked
 *      `enforcedHere: false` with the layer that would own it. A reader
 *      can tell at a glance which half they are looking at.
 *
 * The §14 pilgrim view is built on top of this rather than beside it:
 * the citizen projection is produced by running the operational data
 * through `redactFor("PUBLIC")`, so a field can only reach the public view
 * if it survives an enforced filter. Writing a separate hand-curated
 * citizen payload would mean two places to leak from.
 * ────────────────────────────────────────────────────────────────────────── */

import { CLASSIFICATION } from "../geo/data/provenance";

/* ── Roles ─────────────────────────────────────────────────────────────
 * Ordered by clearance. Ordering matters: access is a comparison, not a
 * lookup table that someone will forget to update. */
export const ROLES = {
  PUBLIC: {
    id: "PUBLIC", level: 0,
    label: { en: "Public / pilgrim", mr: "नागरिक / यात्रेकरू" },
    maxClassification: CLASSIFICATION.PUBLIC,
  },
  FIELD: {
    id: "FIELD", level: 1,
    label: { en: "Field officer", mr: "क्षेत्र अधिकारी" },
    maxClassification: CLASSIFICATION.INTERNAL,
  },
  DEPARTMENT: {
    id: "DEPARTMENT", level: 2,
    label: { en: "Department officer", mr: "विभाग अधिकारी" },
    maxClassification: CLASSIFICATION.INTERNAL,
  },
  COMMAND: {
    id: "COMMAND", level: 3,
    label: { en: "Command centre", mr: "कमांड सेंटर" },
    maxClassification: CLASSIFICATION.RESTRICTED,
  },
  AUDIT: {
    id: "AUDIT", level: 3,
    label: { en: "Audit / oversight", mr: "लेखापरीक्षण" },
    maxClassification: CLASSIFICATION.RESTRICTED,
    /* Read-only by construction — an auditor who can change the record is
     * not an auditor. */
    readOnly: true,
  },
};

const CLASS_LEVEL = {
  [CLASSIFICATION.PUBLIC]: 0,
  [CLASSIFICATION.INTERNAL]: 1,
  [CLASSIFICATION.RESTRICTED]: 2,
};

/* ── Purposes ──────────────────────────────────────────────────────────
 * Purpose limitation only means something if the purpose is recorded and
 * checked. Each purpose declares what it may touch; a request outside that
 * list is refused even when the role's clearance would allow it. */
export const PURPOSES = {
  CROWD_SAFETY: {
    id: "CROWD_SAFETY",
    label: { en: "Crowd safety operations", mr: "गर्दी सुरक्षा" },
    allows: ["aggregate_crowd", "sector_state", "asset_state", "incident", "forecast"],
  },
  EMERGENCY_RESPONSE: {
    id: "EMERGENCY_RESPONSE",
    label: { en: "Emergency response", mr: "आपत्कालीन प्रतिसाद" },
    allows: ["aggregate_crowd", "sector_state", "asset_state", "incident", "unit_position", "medical_capacity"],
  },
  PUBLIC_INFORMATION: {
    id: "PUBLIC_INFORMATION",
    label: { en: "Public information", mr: "जनमाहिती" },
    allows: ["aggregate_crowd", "sector_state", "public_advisory", "facility_location"],
  },
  AUDIT: {
    id: "AUDIT",
    label: { en: "Audit and oversight", mr: "लेखापरीक्षण" },
    allows: ["incident", "decision_record", "event_log", "sector_state"],
  },
};

/* ── Data categories ───────────────────────────────────────────────────
 * Each declares its classification, whether it is personal data, and its
 * retention. The categories the platform refuses to hold at all are listed
 * too — a prohibition that is not written down is not a prohibition. */
export const DATA_CATEGORIES = {
  aggregate_crowd:   { classification: CLASSIFICATION.PUBLIC,     personal: false, retentionDays: 365, note: "Counts and densities. No individual is identifiable." },
  sector_state:      { classification: CLASSIFICATION.PUBLIC,     personal: false, retentionDays: 365, note: "Sector occupancy band and status." },
  facility_location: { classification: CLASSIFICATION.PUBLIC,     personal: false, retentionDays: null, note: "Static. Hospitals, water points, help centres." },
  public_advisory:   { classification: CLASSIFICATION.PUBLIC,     personal: false, retentionDays: 90,  note: "Issued public messages." },
  forecast:          { classification: CLASSIFICATION.INTERNAL,   personal: false, retentionDays: 90,  note: "Model projections. Internal to avoid public misreading of a simulated figure." },
  asset_state:       { classification: CLASSIFICATION.INTERNAL,   personal: false, retentionDays: 365, note: "Gate, barricade and corridor configuration." },
  medical_capacity:  { classification: CLASSIFICATION.INTERNAL,   personal: false, retentionDays: 30,  note: "Bed and bay availability. Aggregate only, never patient-level." },
  incident:          { classification: CLASSIFICATION.INTERNAL,   personal: false, retentionDays: 1825, note: "Operational incidents. May contain a reporter reference but not identity." },
  unit_position:     { classification: CLASSIFICATION.RESTRICTED, personal: false, retentionDays: 30,  note: "Deployment positions. Restricted because publishing them would tell anyone where response is thin." },
  decision_record:   { classification: CLASSIFICATION.RESTRICTED, personal: true,  retentionDays: 1825, note: "Names the authorising officer. Personal data held on the legal basis of public-task accountability." },
  event_log:         { classification: CLASSIFICATION.RESTRICTED, personal: true,  retentionDays: 1825, note: "Append-only audit trail including operator identity." },
};

/* Categories the platform will not hold. Refusal is implemented, not
 * merely intended: `assertPermittedCategory` throws on these. */
export const PROHIBITED_CATEGORIES = {
  biometric_identity: "Face recognition and biometric identification are out of scope. The platform is built for anonymous aggregate crowd analytics.",
  individual_tracking: "Tracking a named individual across the theatre is out of scope and would require a separate legal basis and authority.",
  device_identifiers: "IMSI, MAC and other device identifiers are not collected. They would make an anonymous crowd count into personal data.",
  pilgrim_registry: "No register of pilgrims is held. Attendance at a religious gathering is sensitive personal data.",
  religious_profiling: "No inference about a person's religious practice, caste or community is made or stored.",
};

export class ProhibitedDataError extends Error {}

export function assertPermittedCategory(category) {
  if (PROHIBITED_CATEGORIES[category]) {
    throw new ProhibitedDataError(PROHIBITED_CATEGORIES[category]);
  }
  return true;
}

/* ── Access decision ───────────────────────────────────────────────────
 * Three independent gates, all of which must pass. Keeping them separate
 * means a widened role cannot quietly widen purpose, which is the usual
 * way this kind of control fails. */
export function canAccess(roleId, category, purposeId) {
  const role = ROLES[roleId];
  const cat = DATA_CATEGORIES[category];
  const purpose = PURPOSES[purposeId];

  /* Prohibition is checked FIRST, before the category is even resolved.
   * A banned category is deliberately absent from DATA_CATEGORIES, so
   * checking "unknown" first would refuse it for the wrong reason —
   * "unknown category" reads like a typo somebody might go and fix, where
   * "prohibited by design" does not. */
  if (PROHIBITED_CATEGORIES[category]) {
    return { allowed: false, reason: PROHIBITED_CATEGORIES[category], prohibited: true };
  }

  if (!role)    return { allowed: false, reason: `Unknown role "${roleId}".` };
  if (!cat)     return { allowed: false, reason: `Unknown data category "${category}".` };
  if (!purpose) return { allowed: false, reason: `Unknown purpose "${purposeId}". Access requires a declared purpose.` };
  if (CLASS_LEVEL[cat.classification] > CLASS_LEVEL[role.maxClassification]) {
    return {
      allowed: false,
      reason: `${cat.classification} data is above the ${role.label.en} clearance (${role.maxClassification}).`,
    };
  }
  if (!purpose.allows.includes(category)) {
    return {
      allowed: false,
      reason: `"${category}" is not within the declared purpose "${purpose.label.en}". Clearance alone is not authorisation.`,
      purposeViolation: true,
    };
  }
  return { allowed: true, classification: cat.classification, personal: cat.personal };
}

/* ── Field-level redaction ─────────────────────────────────────────────
 * This is the part that must actually remove data rather than hide it.
 * A UI that renders a RESTRICTED field with `display:none` has still sent
 * it to the browser; this deletes it from the object before it travels.
 *
 * Fields are classified by name because the operational records in this
 * platform are plain objects assembled from several engines. A real
 * deployment would carry classification on the schema. */
const FIELD_CLASSIFICATION = {
  RESTRICTED: [
    "officerId", "officerName", "authorisedBy", "operator", "operatorId",
    "unitPosition", "responderPositions", "deploymentStrength", "strength",
    "reporterId", "reporterPhone", "contact", "phone", "email",
    "hash", "prevHash", "chainDigest",
  ],
  INTERNAL: [
    "forecast", "horizons", "netPerMin", "inflowPerMin", "egressCapacityPerMin",
    "ingressEgressRatio", "interventions", "recommendation", "decisions",
    "emergencyFree", "bedsFree", "occupied", "assumptions",
  ],
};

function fieldClassification(key) {
  if (FIELD_CLASSIFICATION.RESTRICTED.includes(key)) return CLASSIFICATION.RESTRICTED;
  if (FIELD_CLASSIFICATION.INTERNAL.includes(key)) return CLASSIFICATION.INTERNAL;
  return CLASSIFICATION.PUBLIC;
}

/* Returns a deep copy with everything above the role's clearance removed,
 * plus a manifest of what was removed. The manifest matters: a redaction
 * nobody can see is indistinguishable from data that never existed, and
 * an auditor needs to know the difference. */
export function redactFor(roleId, record, { path = "" } = {}) {
  const role = ROLES[roleId];
  if (!role) throw new Error(`Unknown role "${roleId}".`);
  const removed = [];

  const walk = (value, prefix) => {
    if (Array.isArray(value)) return value.map((v, i) => walk(v, `${prefix}[${i}]`));
    if (value === null || typeof value !== "object") return value;
    if (value instanceof Date) return value;

    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const cls = fieldClassification(k);
      if (CLASS_LEVEL[cls] > CLASS_LEVEL[role.maxClassification]) {
        removed.push({ field: prefix ? `${prefix}.${k}` : k, classification: cls });
        return;   // deleted, not masked
      }
      out[k] = walk(v, prefix ? `${prefix}.${k}` : k);
    });
    return out;
  };

  return { data: walk(record, path), removed, role: role.id };
}

/* ── Retention ─────────────────────────────────────────────────────────── */
export function retentionFor(category) {
  const cat = DATA_CATEGORIES[category];
  if (!cat) return { known: false, reason: `Unknown category "${category}".` };
  return {
    known: true,
    days: cat.retentionDays,
    indefinite: cat.retentionDays === null,
    personal: cat.personal,
    note: cat.note,
  };
}

export function isExpired(category, createdAtMs, nowMs = Date.now()) {
  const r = retentionFor(category);
  if (!r.known || r.indefinite) return false;
  return nowMs - createdAtMs > r.days * 86400000;
}

/* ── Declared-but-not-enforced controls ────────────────────────────────
 * The honest half. Each names the layer that would own it, so a reviewer
 * can see this is a gap in the deployment rather than an oversight in the
 * design. */
export const ARCHITECTURAL_CONTROLS = [
  { control: "Encryption in transit",    enforcedHere: true,  owner: "Browser / TLS", note: "Tiles and assets are fetched over HTTPS." },
  { control: "Encryption at rest",       enforcedHere: false, owner: "Database / storage layer", note: "Browser localStorage is not encrypted. No operational data should be held there in a real deployment." },
  { control: "Role-based access",        enforcedHere: true,  owner: "This module", note: "canAccess() gates on clearance, purpose and prohibition." },
  { control: "Attribute-based access",   enforcedHere: true,  owner: "This module", note: "Purpose is an attribute of the request, checked independently of role." },
  { control: "Data minimisation",        enforcedHere: true,  owner: "This module", note: "redactFor() deletes out-of-clearance fields before they travel." },
  { control: "Purpose limitation",       enforcedHere: true,  owner: "This module", note: "Access outside a declared purpose is refused even with sufficient clearance." },
  { control: "Departmental boundaries",  enforcedHere: false, owner: "Identity provider / API gateway", note: "Agency ownership is modelled in the twin, but there is no per-department authentication here." },
  { control: "Retention controls",       enforcedHere: true,  owner: "This module", note: "Per-category retention periods with isExpired(); actual deletion is the storage layer's job." },
  { control: "Immutable audit",          enforcedHere: false, owner: "Append-only database", note: "The browser event log is hash-chained but a browser store can always be cleared. It is labelled 'not an audit record' throughout." },
  { control: "Consent / legal basis",    enforcedHere: true,  owner: "This module", note: "Personal categories name their basis; no consent-based processing is performed." },
  { control: "Human authority",          enforcedHere: true,  owner: "Incident state machine", note: "No transition to dispatch is possible without a recorded authorisation." },
  { control: "Model governance",         enforcedHere: true,  owner: "Assurance layer", note: "Assumptions published per engine; accuracy and drift measured where measurable." },
  { control: "Security monitoring",      enforcedHere: false, owner: "SOC / SIEM", note: "Out of scope for a browser demonstration." },
  { control: "Incident response",        enforcedHere: false, owner: "Operational security team", note: "A process, not a code artefact." },
  { control: "Anonymous over identity",  enforcedHere: true,  owner: "This module", note: "Five identity-analytics categories are prohibited and throw on access." },
];

export function privacyPosture() {
  const enforced = ARCHITECTURAL_CONTROLS.filter((c) => c.enforcedHere);
  const declared = ARCHITECTURAL_CONTROLS.filter((c) => !c.enforcedHere);
  return {
    controls: ARCHITECTURAL_CONTROLS,
    enforced: enforced.length,
    declared: declared.length,
    total: ARCHITECTURAL_CONTROLS.length,
    prohibitedCategories: Object.keys(PROHIBITED_CATEGORIES).length,
    personalCategories: Object.entries(DATA_CATEGORIES)
      .filter(([, c]) => c.personal)
      .map(([k]) => k),
    statement:
      `${enforced.length} of ${ARCHITECTURAL_CONTROLS.length} controls are enforced in code here. `
      + `${declared.length} are architectural and belong to a deployment layer this demonstration does not have — they are named rather than claimed. `
      + `${Object.keys(PROHIBITED_CATEGORIES).length} identity-analytics categories are prohibited outright and throw if requested.`,
  };
}

export const PRIVACY_ASSUMPTIONS = [
  "Redaction deletes fields rather than hiding them. A UI that renders a restricted field invisibly has still sent it to the browser.",
  "Clearance and purpose are checked independently. A widened role must not quietly widen what it may be used for.",
  "Fields are classified by name here because records are assembled from several engines. A real deployment carries classification on the schema instead.",
  "Retention periods are declared and checkable, but deletion is the storage layer's responsibility. This module can say a record is expired; it cannot erase it.",
  "Identity analytics is prohibited in code, not by convention: requesting a prohibited category raises an error rather than returning empty.",
  "Encryption at rest, departmental authentication, immutable audit, security monitoring and incident response cannot be implemented in a browser and are declared as architecture, never claimed as built.",
];
